// pages/communications/InteractionsPage.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatDate } from '../../utils/formatters';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
var C = {
  pri: '#2563eb', priL: '#eff6ff', priD: '#1d4ed8',
  ok: '#10b981', okL: '#ecfdf5',
  warn: '#f59e0b', warnL: '#fffbeb',
  err: '#ef4444', errL: '#fef2f2',
  g50: '#f8fafc', g100: '#f1f5f9', g200: '#e2e8f0', g300: '#cbd5e1',
  g400: '#94a3b8', g500: '#64748b', g600: '#475569',
  g700: '#334155', g800: '#1e293b', g900: '#0f172a',
};

/* ═══════════════════════════════════════════════════════════════
   CONFIG
   ═══════════════════════════════════════════════════════════════ */
var METHOD_OPTIONS = [
  { value: 'call', label: 'Call', icon: '\uD83D\uDCDE' },
  { value: 'chat', label: 'Chat', icon: '\uD83D\uDCAC' },
  { value: 'visit', label: 'Visit / Face to Face', icon: '\uD83E\uDD1D' },
  { value: 'sms', label: 'SMS', icon: '\uD83D\uDCAC' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '\uD83D\uDCF1' },
  { value: 'email', label: 'Email', icon: '\u2709\uFE0F' },
  { value: 'other', label: 'Other', icon: '\uD83D\uDCCC' },
];

var PURPOSE_OPTIONS = [
  { value: 'Customer follow up', label: 'Customer follow up' },
  { value: 'Customer reactivation/follow up', label: 'Customer reactivation/follow up' },
  { value: 'Lead Follow Up', label: 'Lead Follow Up' },
  { value: 'Collateral list Confirmation', label: 'Collateral list Confirmation' },
  { value: 'Surprise Visit', label: 'Surprise Visit' },
  { value: 'Spoof calling', label: 'Spoof calling' },
  { value: 'Promise to pay', label: 'Promise to pay' },
  { value: 'Loan application follow up', label: 'Loan application follow up' },
  { value: 'Document collection', label: 'Document collection' },
  { value: 'Payment reminder', label: 'Payment reminder' },
  { value: 'Default management', label: 'Default management' },
  { value: 'General inquiry', label: 'General inquiry' },
  { value: 'Complaint resolution', label: 'Complaint resolution' },
  { value: 'Other', label: 'Other' },
];

var OUTCOME_OPTIONS = [
  { value: 'Contactable', label: 'Contactable', color: C.ok },
  { value: 'Promise to Pay', label: 'Promise to Pay', color: '#2563eb' },
  { value: 'Said Call Me Later', label: 'Said Call Me Later', color: '#f59e0b' },
  { value: 'Not Contactable', label: 'Not Contactable', color: C.err },
  { value: 'Wrong Number', label: 'Wrong Number', color: C.err },
  { value: 'Refused to Pay', label: 'Refused to Pay', color: C.err },
  { value: 'Partial Payment Promised', label: 'Partial Payment Promised', color: '#f59e0b' },
  { value: 'Paid in Full', label: 'Paid in Full', color: C.ok },
  { value: 'Visited \u2014 Not Found', label: 'Visited \u2014 Not Found', color: C.err },
  { value: 'Visited \u2014 Found', label: 'Visited \u2014 Found', color: C.ok },
  { value: 'No Response', label: 'No Response', color: C.g400 },
  { value: 'Voicemail', label: 'Voicemail', color: C.g400 },
  { value: 'Other', label: 'Other', color: C.g500 },
];

var NEXT_STEPS_OPTIONS = [
  { value: 'Call again', label: 'Call again' },
  { value: 'Visit again', label: 'Visit again' },
  { value: 'Send SMS reminder', label: 'Send SMS reminder' },
  { value: 'Send WhatsApp message', label: 'Send WhatsApp message' },
  { value: 'Send email', label: 'Send email' },
  { value: 'Escalate to manager', label: 'Escalate to manager' },
  { value: 'Hand over to collections', label: 'Hand over to collections' },
  { value: 'Close \u2014 Resolved', label: 'Close \u2014 Resolved' },
  { value: 'Schedule meeting', label: 'Schedule meeting' },
  { value: 'Follow up after payment', label: 'Follow up after payment' },
  { value: 'No further action', label: 'No further action' },
  { value: 'Other', label: 'Other' },
];

var METHOD_CFG = {};
METHOD_OPTIONS.forEach(function (o) { METHOD_CFG[o.value] = o; });

var OUTCOME_CFG = {};
OUTCOME_OPTIONS.forEach(function (o) { OUTCOME_CFG[o.value] = o; });

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */
function getMethod(item) {
  var method = item.conversationMethod || item.type || 'other';
  if (method === 'note' || method === 'in-person') method = method === 'in-person' ? 'visit' : 'other';
  return method;
}

function getPurpose(item) {
  // Onboarding interaction prompts store the prompt/step title instead of
  // the structured Communication purpose. Keep that typed context visible.
  return item.conversationPurpose || item.stepTitle || '';
}

function getOutcome(item) {
  return item.conversationOutcome || '';
}

function getNextSteps(item) {
  return item.nextSteps || '';
}

function getOutcomeColor(outcome) {
  var cfg = OUTCOME_CFG[outcome];
  return cfg ? cfg.color : C.g500;
}

function getClientName(item) {
  if (item.client && typeof item.client === 'object') {
    return ((item.client.firstName || '') + ' ' + (item.client.lastName || '')).trim();
  }
  return 'Unknown';
}

function getClientNumber(item) {
  if (item.client && typeof item.client === 'object') return item.client.clientNumber || '';
  return '';
}

function getClientPhone(item) {
  if (item.client && typeof item.client === 'object') return item.client.phone || '';
  return '';
}

function getClientId(item) {
  if (item.client) return typeof item.client === 'object' ? item.client._id : item.client;
  return null;
}

function getUserName(item) {
  var u = item.sentBy || item.recordedBy || null;
  if (!u) return '';
  return (u.firstName || '') + ' ' + (u.lastName || '');
}

function getUserId(item) {
  var u = item.sentBy || null;
  return u ? (u._id || null) : null;
}

function getBranchName(item) {
  if (item.branch && typeof item.branch === 'object') return item.branch.name || '';
  if (item.client && typeof item.client === 'object' && item.client.branch && typeof item.client.branch === 'object') return item.client.branch.name || '';
  return '';
}


function getOfficerRole(item) {
  return item.officerRole || '';
}

function isSameDay(dateStr, targetDateStr) {
  if (!dateStr) return false;
  try {
    var d = new Date(dateStr);
    var t = new Date(targetDateStr + 'T00:00:00.000Z');
    return d.getUTCFullYear() === t.getUTCFullYear() && d.getUTCMonth() === t.getUTCMonth() && d.getUTCDate() === t.getUTCDate();
  } catch (e) { return false; }
}

function isSameWeek(dateStr) {
  if (!dateStr) return false;
  try {
    var d = new Date(dateStr);
    var now = new Date();
    var startOfWeek = new Date(now);
    startOfWeek.setHours(0, 0, 0, 0);
    var day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
    return d >= startOfWeek;
  } catch (e) { return false; }
}

function relativeTime(d) {
  if (!d) return '';
  var diff = Date.now() - new Date(d).getTime();
  var m = Math.floor(diff / 60000);
  var h = Math.floor(diff / 3600000);
  var day = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return m + 'm ago';
  if (h < 24) return h + 'h ago';
  if (day < 7) return day + 'd ago';
  return formatDate(d);
}

function generatePageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, function (_, i) { return i + 1; });
  var pages = [];
  if (current <= 4) { for (var i = 1; i <= 5; i++) pages.push(i); pages.push('...', total); }
  else if (current >= total - 3) { pages.push(1, '...'); for (var j = total - 4; j <= total; j++) pages.push(j); }
  else { pages.push(1, '...', current - 1, current, current + 1, '...', total); }
  return pages;
}

/* ═══════════════════════════════════════════════════════════════
   TINY COMPONENTS
   ═══════════════════════════════════════════════════════════════ */
function Pill({ label, color, bg, small }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: small ? '2px 8px' : '3px 10px', borderRadius: 20,
      fontSize: small ? 10 : 11, fontWeight: 600, color: color,
      background: bg, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function Skeleton({ w, h, r }) {
  return (
    <div style={{
      width: w || '100%', height: h || 16, borderRadius: r || 6,
      background: 'linear-gradient(90deg, ' + C.g200 + ' 25%, ' + C.g100 + ' 50%, ' + C.g200 + ' 75%)',
      backgroundSize: '200% 100%', animation: 'skel 1.5s ease-in-out infinite',
    }} />
  );
}

function StatCard({ icon, label, value, sub, bg, accent, trend }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200,
      overflow: 'hidden', position: 'relative', transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={function (e) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
      onMouseLeave={function (e) { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {accent && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accent }} />}
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: C.g400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
            <p style={{ margin: '4px 0 0', fontSize: 28, fontWeight: 800, color: C.g800, lineHeight: 1 }}>{value}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              {sub && <p style={{ margin: 0, fontSize: 10, color: C.g400 }}>{sub}</p>}
              {trend !== undefined && trend !== 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                  color: trend > 0 ? C.ok : C.err,
                  background: trend > 0 ? C.okL : C.errL,
                }}>{trend > 0 ? '\u25B2' : '\u25BC'} {Math.abs(trend)}</span>
              )}
            </div>
          </div>
          <div style={{
            width: 42, height: 42, borderRadius: 12, background: bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>{icon}</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   OFFICER CARD
   ═══════════════════════════════════════════════════════════════ */
function OfficerCard({ officer, interactions, clients, role, date }) {
  var roleLabel = role === 'loan-officer' ? '\uD83C\uDFE6 Loan Officer' : role === 'collection-officer' ? '\uD83D\uDCB0 Collection Officer' : '\uD83D\uDC64 Other';
  var roleColor = role === 'loan-officer' ? '#1d4ed8' : role === 'collection-officer' ? '#15803d' : '#64748b';
  var roleBg = role === 'loan-officer' ? '#eff6ff' : role === 'collection-officer' ? '#f0fdf4' : '#f1f5f9';
  var gradFrom = roleColor + '20';
  var gradTo = roleColor + '40';
  var borderCol = roleColor + '30';

  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200,
      overflow: 'hidden', transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={function (e) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
      onMouseLeave={function (e) { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'linear-gradient(135deg, ' + gradFrom + ', ' + gradTo + ')',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, color: roleColor, border: '2px solid ' + borderCol,
          }}>
            {(officer.firstName || 'U')[0]}{(officer.lastName || '')[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.g800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {officer.firstName} {officer.lastName}
            </p>
            <Pill label={roleLabel} color={roleColor} bg={roleBg} small />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ padding: '10px 12px', background: C.g50, borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.g400, textTransform: 'uppercase' }}>Interactions</p>
            <p style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 800, color: C.g800 }}>{interactions}</p>
          </div>
          <div style={{ padding: '10px 12px', background: C.g50, borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.g400, textTransform: 'uppercase' }}>Clients</p>
            <p style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 800, color: C.ok }}>{clients}</p>
          </div>
        </div>
        {date && <p style={{ margin: '10px 0 0', fontSize: 10, color: C.g400, textAlign: 'center' }}>{'\uD83D\uDCC5'} {date}</p>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CUSTOM SELECT
   ═══════════════════════════════════════════════════════════════ */
function FormSelect({ label, value, onChange, options, placeholder, required }) {
  var _open = useState(false);
  var open = _open[0]; var setOpen = _open[1];
  var _hover = useState(-1);
  var hoverIdx = _hover[0]; var setHoverIdx = _hover[1];
  var ref = useRef(null);

  useEffect(function () {
    function handleClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  var selectedCfg = options.find(function (o) { return o.value === value; });
  var displayText = selectedCfg
    ? (selectedCfg.icon ? selectedCfg.icon + ' ' : '') + selectedCfg.label
    : placeholder || 'Select One';

  return (
    <div ref={ref} style={{ flex: 1, position: 'relative' }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
          {label} {required && <span style={{ color: C.err }}>*</span>}
        </label>
      )}
      <div
        onClick={function () { setOpen(!open); }}
        style={{
          width: '100%', padding: '10px 36px 10px 14px', borderRadius: 8,
          border: '2px solid ' + (open ? C.pri : C.g200),
          fontSize: 14, color: value ? C.g800 : C.g400, cursor: 'pointer',
          background: '#fff', userSelect: 'none', transition: 'border-color 0.15s',
          boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
        }}
      >
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
          {displayText}
        </span>
        <span style={{
          position: 'absolute', right: 12, top: label ? 'calc(50% + 10px)' : '50%',
          transform: 'translateY(-50%)', fontSize: 10, color: C.g400,
        }}>{open ? '\u25B2' : '\u25BC'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 100, left: 0, right: 0, marginTop: 4,
          background: '#fff', border: '1px solid ' + C.g200, borderRadius: 10,
          boxShadow: '0 12px 36px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto',
        }}>
          {options.map(function (opt, idx) {
            var isSel = opt.value === value;
            return (
              <div key={opt.value}
                onClick={function (e) { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
                onMouseEnter={function () { setHoverIdx(idx); }}
                onMouseLeave={function () { setHoverIdx(-1); }}
                style={{
                  padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                  color: isSel ? C.pri : C.g700,
                  background: isSel ? C.priL : (hoverIdx === idx ? C.g50 : '#fff'),
                  fontWeight: isSel ? 600 : 400,
                  borderBottom: idx < options.length - 1 ? '1px solid ' + C.g100 : 'none',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {opt.icon && <span style={{ fontSize: 15 }}>{opt.icon}</span>}
                {opt.label}
                {isSel && <span style={{ marginLeft: 'auto', color: C.pri, fontSize: 12 }}>{'\u2713'}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ADD INTERACTION MODAL
   ═══════════════════════════════════════════════════════════════ */
function AddModal({ open, onClose, onSaved }) {
  var _step = useState('select-client');
  var step = _step[0]; var setStep = _step[1];
  var _search = useState('');
  var search = _search[0]; var setSearch = _search[1];
  var _clients = useState([]);
  var clients = _clients[0]; var setClients = _clients[1];
  var _searching = useState(false);
  var searching = _searching[0]; var setSearching = _searching[1];
  var _sel = useState(null);
  var selectedClient = _sel[0]; var setSelectedClient = _sel[1];
  var _form = useState({
    conversationMethod: 'call',
    conversationPurpose: 'Customer follow up',
    conversationOutcome: 'Contactable',
    content: '',
    nextSteps: 'Call again',
    nextInteractionDate: '',
  });
  var form = _form[0]; var setForm = _form[1];
  var _saving = useState(false);
  var saving = _saving[0]; var setSaving = _saving[1];
  var searchTimer = useRef(null);

  useEffect(function () {
    if (open) {
      setForm({
        conversationMethod: 'call',
        conversationPurpose: 'Customer follow up',
        conversationOutcome: 'Contactable',
        content: '',
        nextSteps: 'Call again',
        nextInteractionDate: '',
      });
      setStep('select-client');
      setSelectedClient(null);
      setSearch('');
      setClients([]);
    }
  }, [open]);

  var searchClients = function (q) {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.length < 2) { setClients([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(function () {
      api.get('/clients?search=' + encodeURIComponent(q) + '&limit=10&status=approved')
        .then(function (r) { setClients((r.data.data && r.data.data.clients) || []); })
        .catch(function () { setClients([]); })
        .finally(function () { setSearching(false); });
    }, 350);
  };

  var handleSearch = function (e) { var v = e.target.value; setSearch(v); searchClients(v); };
  var selectClient = function (c) { setSelectedClient(c); setStep('details'); };
  var updateForm = function (field, value) {
    setForm(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n[field] = value; return n; });
  };

  var handleSubmit = function () {
    if (!selectedClient) return;
    setSaving(true);
    api.post('/communications', {
      client: selectedClient._id,
      conversationMethod: form.conversationMethod,
      conversationPurpose: form.conversationPurpose,
      conversationOutcome: form.conversationOutcome,
      content: form.content.trim(),
      nextSteps: form.nextSteps,
      nextInteractionDate: form.nextInteractionDate || null,
    })
      .then(function () { onSaved(); onClose(); })
      .catch(function (err) { alert((err.response && err.response.data && err.response.data.message) || 'Failed to save'); })
      .finally(function () { setSaving(false); });
  };

  if (!open) return null;

  var inputBase = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '2px solid ' + C.g200, fontSize: 14, outline: 'none',
    background: '#fff', color: C.g800, boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
    }} onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '94%', maxWidth: 660,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'zmIn 0.25s ease',
      }} onClick={function (e) { e.stopPropagation(); }}>

        <div style={{
          padding: '18px 24px', borderBottom: '1px solid ' + C.g200,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g800 }}>
              New Interaction with: {selectedClient ? selectedClient.firstName + ' ' + selectedClient.lastName : '...'}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: C.g400 }}>
              {step === 'select-client' ? 'Step 1: Select a client' : 'Fill in the interaction details below'}
            </p>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: 'none',
            background: C.g100, color: C.g500, fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{'\u2715'}</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {step === 'select-client' && (
            <div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>{'\uD83D\uDD0D'}</span>
                <input type="text" value={search} onChange={handleSearch}
                  placeholder="Search client by name, phone, or ID..." autoFocus
                  style={{ ...inputBase, paddingLeft: 42 }}
                  onFocus={function (e) { e.target.style.borderColor = C.pri; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                  onBlur={function (e) { e.target.style.borderColor = C.g200; e.target.style.boxShadow = 'none'; }}
                />
                {searching && (
                  <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>{'\u23F3'}</span>
                  </div>
                )}
              </div>
              {clients.length > 0 && (
                <div style={{ marginTop: 10, border: '1px solid ' + C.g200, borderRadius: 12, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                  {clients.map(function (c, i) {
                    return (
                      <div key={c._id || i} onClick={function () { selectClient(c); }}
                        style={{
                          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                          cursor: 'pointer',
                          borderBottom: i < clients.length - 1 ? '1px solid ' + C.g100 : 'none',
                          background: '#fff', transition: 'background 0.15s',
                        }}
                        onMouseEnter={function (e) { e.currentTarget.style.background = C.g50; }}
                        onMouseLeave={function (e) { e.currentTarget.style.background = '#fff'; }}
                      >
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', background: C.priL, color: C.pri,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>{(c.firstName || '')[0]}{(c.lastName || '')[0]}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.g800 }}>{c.firstName} {c.lastName}</p>
                          <p style={{ margin: 0, fontSize: 12, color: C.g400 }}>{c.clientNumber || '\u2014'} {'\u00B7'} {c.phone || '\u2014'}</p>
                        </div>
                        <div style={{ fontSize: 18, color: C.g300 }}>{'\u2192'}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!searching && search.length >= 2 && clients.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: C.g400, fontSize: 13 }}>
                  <p style={{ fontSize: 28, marginBottom: 8 }}>{'\uD83D\uDD0D'}</p>
                  No clients found matching &quot;<strong>{search}</strong>&quot;
                </div>
              )}
            </div>
          )}

          {step === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{
                padding: '12px 16px', background: C.priL, borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #bfdbfe',
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', background: C.pri, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>{(selectedClient.firstName || '')[0]}{(selectedClient.lastName || '')[0]}</div>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.g800 }}>{selectedClient.firstName} {selectedClient.lastName}</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.g500 }}>{selectedClient.clientNumber} {'\u00B7'} {selectedClient.phone}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
                <FormSelect label="Conversation Method" value={form.conversationMethod}
                  onChange={function (v) { updateForm('conversationMethod', v); }}
                  options={METHOD_OPTIONS} placeholder="Select One" required />
                <FormSelect label="Conversation Purpose" value={form.conversationPurpose}
                  onChange={function (v) { updateForm('conversationPurpose', v); }}
                  options={PURPOSE_OPTIONS} placeholder="Select One" required />
                <FormSelect label="Conversation Outcome" value={form.conversationOutcome}
                  onChange={function (v) { updateForm('conversationOutcome', v); }}
                  options={OUTCOME_OPTIONS} placeholder="Select One" required />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>Outcome / Details</label>
                <textarea value={form.content}
                  onChange={function (e) { updateForm('content', e.target.value); }}
                  placeholder="Describe the interaction outcome and details..." rows={4}
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                  onFocus={function (e) { e.target.style.borderColor = C.pri; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                  onBlur={function (e) { e.target.style.borderColor = C.g200; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>Next Interaction</label>
                  <input type="date" value={form.nextInteractionDate}
                    onChange={function (e) { updateForm('nextInteractionDate', e.target.value); }}
                    style={{ ...inputBase, appearance: 'auto' }}
                    onFocus={function (e) { e.target.style.borderColor = C.pri; }}
                    onBlur={function (e) { e.target.style.borderColor = C.g200; }}
                  />
                </div>
                <FormSelect label="Next Steps" value={form.nextSteps}
                  onChange={function (v) { updateForm('nextSteps', v); }}
                  options={NEXT_STEPS_OPTIONS} placeholder="Select One" />
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid ' + C.g200, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          {step === 'details' && (
            <button onClick={function () { setStep('select-client'); }} disabled={saving}
              style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + C.g200, background: '#fff', color: C.g600, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {'\u2190'} Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={saving}
            style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + C.g200, background: '#fff', color: C.g600, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer' }}>
            Cancel
          </button>
          {step === 'select-client' ? (
            <button onClick={function () { if (selectedClient) setStep('details'); }} disabled={!selectedClient || saving}
              style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: (!selectedClient || saving) ? C.g300 : C.pri, color: '#fff', fontSize: 14, fontWeight: 600, cursor: (!selectedClient || saving) ? 'not-allowed' : 'pointer', boxShadow: (!selectedClient || saving) ? 'none' : '0 4px 12px rgba(37,99,235,0.3)' }}>
              Next {'\u2192'}
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={saving}
              style={{ padding: '10px 28px', borderRadius: 10, border: 'none', background: saving ? C.g300 : C.pri, color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: saving ? 'none' : '0 4px 12px rgba(37,99,235,0.3)' }}>
              {saving
                ? <span><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>{'\u23F3'}</span> Saving...</span>
                : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TABLE STYLES
   ═══════════════════════════════════════════════════════════════ */
var thStyle = {
  padding: '10px 14px', fontSize: 10, fontWeight: 700, color: C.g500,
  textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left',
  whiteSpace: 'nowrap', borderBottom: '2px solid ' + C.g200, background: C.g50,
};

var tdStyle = {
  padding: '10px 14px', fontSize: 12, verticalAlign: 'middle', whiteSpace: 'nowrap',
  borderBottom: '1px solid ' + C.g100,
};

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function InteractionsPage() {
  var nav = useNavigate();
  var PER_PAGE = 30;

  var _data = useState([]);
  var interactions = _data[0]; var setInteractions = _data[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];
  var _total = useState(0);
  var totalCount = _total[0]; var setTotalCount = _total[1];
  var _interactionTotal = useState(0);
  var interactionTotalCount = _interactionTotal[0]; var setInteractionTotalCount = _interactionTotal[1];
  var _modal = useState(false);
  var modalOpen = _modal[0]; var setModalOpen = _modal[1];
  var _showOff = useState(true);
  var showOfficerPanel = _showOff[0]; var setShowOfficerPanel = _showOff[1];
  var _officerLoading = useState(false);
  var officerLoading = _officerLoading[0]; var setOfficerLoading = _officerLoading[1];
  var _expandedClient = useState(null);
  var expandedClientId = _expandedClient[0]; var setExpandedClientId = _expandedClient[1];
  var _clientHistories = useState({});
  var clientHistories = _clientHistories[0]; var setClientHistories = _clientHistories[1];
  var _clientHistoryLoading = useState({});
  var clientHistoryLoading = _clientHistoryLoading[0]; var setClientHistoryLoading = _clientHistoryLoading[1];
  var _exporting = useState(false);
  var exporting = _exporting[0]; var setExporting = _exporting[1];

  var _filters = useState({
    search: '', conversationMethod: '', conversationPurpose: '',
    conversationOutcome: '', startDate: '', endDate: '', page: 1,
  });
  var filters = _filters[0]; var setFilters = _filters[1];
  var _statsDate = useState(new Date().toISOString().split('T')[0]);
  var statsDate = _statsDate[0]; var setStatsDate = _statsDate[1];

  /* ── Load interactions from communications endpoint only ── */
  var loadInteractions = useCallback(function () {
    setLoading(true);
    var params = ['limit=' + PER_PAGE, 'page=' + filters.page, 'groupByClient=true'];
    if (filters.search) params.push('search=' + encodeURIComponent(filters.search));
    if (filters.conversationMethod) params.push('conversationMethod=' + filters.conversationMethod);
    if (filters.conversationPurpose) params.push('conversationPurpose=' + filters.conversationPurpose);
    if (filters.conversationOutcome) params.push('conversationOutcome=' + filters.conversationOutcome);
    if (filters.startDate) params.push('startDate=' + filters.startDate);
    if (filters.endDate) params.push('endDate=' + filters.endDate);

    api.get('/communications?' + params.join('&'))
      .then(function (r) {
        var d = r.data;
        setInteractions(Array.isArray(d.data) ? d.data : []);
        setTotalCount(d.pagination ? d.pagination.total : (Array.isArray(d.data) ? d.data.length : 0));
        setInteractionTotalCount(d.interactionTotal || 0);
        setExpandedClientId(null);
      })
      .catch(function () { setInteractions([]); setTotalCount(0); setInteractionTotalCount(0); })
      .finally(function () { setLoading(false); });
  }, [filters]);

  useEffect(function () { loadInteractions(); }, [loadInteractions]);

  var handleModalSaved = useCallback(function () {
    setFilters(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n.page = 1; return n; });
  }, []);

  var toggleClient = useCallback(function (clientId) {
    if (!clientId) return;
    if (expandedClientId === clientId) {
      setExpandedClientId(null);
      return;
    }
    setExpandedClientId(clientId);
    if (clientHistories[clientId]) return;
    setClientHistoryLoading(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[clientId] = true; return n; });
    api.get('/communications/client/' + clientId + '?limit=500')
      .then(function (r) {
        var data = r.data && Array.isArray(r.data.data) ? r.data.data : [];
        setClientHistories(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[clientId] = data; return n; });
      })
      .catch(function () {
        setClientHistories(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[clientId] = []; return n; });
      })
      .finally(function () {
        setClientHistoryLoading(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[clientId] = false; return n; });
      });
  }, [expandedClientId, clientHistories]);

  /* ── Export all matching interactions to Excel ── */
  var exportInteractions = useCallback(async function () {
    setExporting(true);
    try {
      var pageSize = 1000;
      var pageNo = 1;
      var total = 0;
      var allRows = [];

      // Export the flat interaction records, not the grouped client summary.
      // This preserves every interaction in the workbook while applying the
      // same filters used by the page.
      do {
        var params = [
          'limit=' + pageSize,
          'page=' + pageNo,
          'groupByClient=false',
        ];
        if (filters.search) params.push('search=' + encodeURIComponent(filters.search));
        if (filters.conversationMethod) params.push('conversationMethod=' + encodeURIComponent(filters.conversationMethod));
        if (filters.conversationPurpose) params.push('conversationPurpose=' + encodeURIComponent(filters.conversationPurpose));
        if (filters.conversationOutcome) params.push('conversationOutcome=' + encodeURIComponent(filters.conversationOutcome));
        if (filters.startDate) params.push('startDate=' + filters.startDate);
        if (filters.endDate) params.push('endDate=' + filters.endDate);

        var pageRes = await api.get('/communications?' + params.join('&'));
        var pageData = pageRes.data || {};
        var rows = Array.isArray(pageData.data) ? pageData.data : [];
        allRows = allRows.concat(rows);
        total = Number(pageData.pagination?.total || allRows.length);
        pageNo += 1;
        if (!rows.length) break;
      } while (allRows.length < total);

      if (!allRows.length) {
        window.alert('No interactions match the selected filters.');
        return;
      }

      var headers = [
        { key: 'createdAt', label: 'Date & Time', render: function (i) { return i.createdAt ? new Date(i.createdAt).toLocaleString() : ''; } },
        { key: 'clientName', label: 'Client Name', render: function (i) { return getClientName(i); } },
        { key: 'clientNumber', label: 'Client Number', render: function (i) { return getClientNumber(i); }, forceString: true },
        { key: 'clientPhone', label: 'Phone', render: function (i) { return getClientPhone(i); }, forceString: true },
        { key: 'method', label: 'Method', render: function (i) { return getMethod(i); } },
        { key: 'purpose', label: 'Purpose', render: function (i) { return getPurpose(i); } },
        { key: 'outcome', label: 'Outcome', render: function (i) { return getOutcome(i); } },
        { key: 'content', label: 'Interaction Details', render: function (i) { return i.content || i.interactionDetails || i.details || i.description || ''; } },
        { key: 'nextSteps', label: 'Next Steps', render: function (i) { return getNextSteps(i) || i.followUp || i.actionRequired || ''; } },
        { key: 'notes', label: 'Additional Notes', render: function (i) { return i.notes || i.internalNotes || i.comment || ''; } },
        { key: 'nextInteractionDate', label: 'Next Interaction', render: function (i) { var d = i.nextInteractionDate || i.nextInteraction || i.followUpDate; return d ? new Date(d).toLocaleDateString() : ''; } },
        { key: 'officer', label: 'Recorded By', render: function (i) { return getUserName(i).trim(); } },
        { key: 'officerRole', label: 'Officer Role', render: function (i) { return getOfficerRole(i); } },
        { key: 'branch', label: 'Branch', render: function (i) { return getBranchName(i); } },
        { key: 'source', label: 'Source', render: function (i) { return i.sourceLabel || i.source || i.origin || ''; } },
      ];

      var response = await api.post('/reports/exports/generate', {
        data: allRows,
        headers: headers,
        filename: 'Client_Interactions_' + new Date().toISOString().slice(0, 10),
        format: 'xlsx',
      }, { responseType: 'blob' });

      var blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'client_interactions_' + new Date().toISOString().slice(0, 10) + '.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Interaction export failed:', err);
      window.alert(err.response?.data?.message || 'Interaction export failed.');
    } finally {
      setExporting(false);
    }
  }, [filters]);

  /* ── Load officer stats from dedicated endpoint ── */
  var _officerData = useState([]);
  var officerData = _officerData[0]; var setOfficerData = _officerData[1];
  var _officerSummary = useState(null);
  var officerSummary = _officerSummary[0]; var setOfficerSummary = _officerSummary[1];

  var loadOfficerStats = useCallback(function () {
    setOfficerLoading(true);
    api.get('/communications/officer-stats?date=' + statsDate)
      .then(function (r) {
        setOfficerData(Array.isArray(r.data.data) ? r.data.data : []);
        setOfficerSummary(r.data.summary || null);
      })
      .catch(function () { setOfficerData([]); setOfficerSummary(null); })
      .finally(function () { setOfficerLoading(false); });
  }, [statsDate]);

  useEffect(function () { loadOfficerStats(); }, [loadOfficerStats]);

  /* ── Stats (lightweight from current page) ── */
  var todayStr = useMemo(function () {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }, []);

  var summary = useMemo(function () {
    var todayCount = interactions.filter(function (i) { return isSameDay(i.createdAt, todayStr); }).length;
    var weekCount = interactions.filter(function (i) { return isSameWeek(i.createdAt); }).length;
    var now = new Date(); now.setHours(0, 0, 0, 0);
    var overdue = interactions.filter(function (i) {
      if (!i.nextInteractionDate) return false;
      try { return new Date(i.nextInteractionDate) < now; } catch (e) { return false; }
    }).length;
    return { todayCount: todayCount, weekCount: weekCount, overdue: overdue, total: interactionTotalCount };
  }, [interactions, todayStr, interactionTotalCount]);

  /* ── Pagination ── */
  var totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  useEffect(function () {
    if (filters.page > totalPages) {
      setFilters(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n.page = totalPages; return n; });
    }
  }, [totalPages, filters.page]);

  var updateFilter = function (key, value) {
    setFilters(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n[key] = value; n.page = 1; return n; });
  };
  var handleSearch = function (e) {
    setFilters(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n.search = e.target.value; n.page = 1; return n; });
  };
  var clearFilters = function () {
    setFilters({ search: '', conversationMethod: '', conversationPurpose: '', conversationOutcome: '', startDate: '', endDate: '', page: 1 });
  };
  var hasActiveFilters = filters.search || filters.conversationMethod || filters.conversationPurpose || filters.conversationOutcome || filters.startDate || filters.endDate;
  var goToPage = function (p) {
    setFilters(function (f) { var n = {}; for (var k in f) n[k] = f[k]; n.page = p; return n; });
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  var selectStyle = {
    padding: '8px 32px 8px 12px', borderRadius: 8, border: '2px solid ' + C.g200,
    fontSize: 13, outline: 'none', background: '#fff', color: C.g700, cursor: 'pointer', appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2394a3b8\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    transition: 'border-color 0.15s',
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div style={{ padding: '20px 24px', maxWidth: 1440, margin: '0 auto' }}>

      <style>{`
        @keyframes skel { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes zmIn { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        ::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px}
        ::-webkit-scrollbar-thumb:hover{background:#94a3b8}
      `}</style>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.g900 }}>Interactions</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.g400 }}>Track all client communications and follow-ups</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={exportInteractions} disabled={exporting} style={{
            padding: '10px 18px', borderRadius: 10, border: '1px solid ' + C.ok,
            background: exporting ? C.g100 : C.okL, color: exporting ? C.g400 : C.ok,
            fontSize: 13, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>{exporting ? '⏳ Exporting...' : '📥 Export Excel'}</button>
          <button onClick={function () { setModalOpen(true); }} style={{
            padding: '10px 22px', borderRadius: 10, border: 'none',
            background: C.pri, color: '#fff', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
          }}>+ New Interaction</button>
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon={'\uD83D\uDCE1'} label="Total Interactions" value={summary.total} sub={totalCount + " clients"} bg={C.priL} accent={C.pri} />
        <StatCard icon={'\uD83D\uDCCB'} label="Today" value={summary.todayCount} bg="#ecfdf5" accent={C.ok} />
        <StatCard icon={'\uD83D\uDCC5'} label="This Week" value={summary.weekCount} bg="#fffbeb" accent="#f59e0b" />
        <StatCard icon={'\u23F0'} label="Overdue" value={summary.overdue} sub="Next interactions" bg={C.errL} accent={C.err} />
      </div>

      {/* ── Filters ── */}
      <div style={{
        background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200,
        padding: '14px 18px', marginBottom: 20,
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
      }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>{'\uD83D\uDD0D'}</span>
          <input type="text" value={filters.search} onChange={handleSearch}
            placeholder="Search by client name, phone, details..."
            style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: 8, border: '2px solid ' + C.g200, fontSize: 13, outline: 'none', color: C.g800, transition: 'border-color 0.15s' }}
            onFocus={function (e) { e.target.style.borderColor = C.pri; }}
            onBlur={function (e) { e.target.style.borderColor = C.g200; }}
          />
        </div>
        <select value={filters.conversationMethod} onChange={function (e) { updateFilter('conversationMethod', e.target.value); }} style={selectStyle}>
          <option value="">All Methods</option>
          {METHOD_OPTIONS.map(function (o) { return <option key={o.value} value={o.value}>{o.icon} {o.label}</option>; })}
        </select>
        <select value={filters.conversationPurpose} onChange={function (e) { updateFilter('conversationPurpose', e.target.value); }} style={selectStyle}>
          <option value="">All Purposes</option>
          {PURPOSE_OPTIONS.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
        </select>
        <select value={filters.conversationOutcome} onChange={function (e) { updateFilter('conversationOutcome', e.target.value); }} style={selectStyle}>
          <option value="">All Outcomes</option>
          {OUTCOME_OPTIONS.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
        </select>
        <input type="date" value={filters.startDate} onChange={function (e) { updateFilter('startDate', e.target.value); }}
          style={{ ...selectStyle, width: 150, appearance: 'auto' }} />
        <input type="date" value={filters.endDate} onChange={function (e) { updateFilter('endDate', e.target.value); }}
          style={{ ...selectStyle, width: 150, appearance: 'auto' }} />
        {hasActiveFilters && (
          <button onClick={clearFilters} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid ' + C.err,
            background: C.errL, color: C.err, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>{'\u2715'} Clear</button>
        )}
      </div>

      {/* ── View Toggle + Officer Panel Toggle ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: C.g500 }}>
          Showing <strong style={{ color: C.g800 }}>{interactions.length}</strong> of {totalCount} clients · <strong style={{ color: C.g800 }}>{interactionTotalCount}</strong> interactions
          {filters.page > 1 && <span> (Page {filters.page} of {totalPages})</span>}
        </p>
        <button onClick={function () { setShowOfficerPanel(!showOfficerPanel); }}
          style={{
            padding: '7px 14px', borderRadius: 8, border: '1px solid ' + C.g200,
            background: showOfficerPanel ? C.priL : '#fff', color: showOfficerPanel ? C.pri : C.g500,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s',
          }}>
          {'\uD83D\uDCCA'} Officer Performance {showOfficerPanel ? '\u25B2' : '\u25BC'}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
         OFFICER PERFORMANCE PANEL
         ═══════════════════════════════════════════════════════════ */}
      {showOfficerPanel && (
        <div style={{
          background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200,
          padding: '20px', marginBottom: 20, overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: C.priL,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              }}>{'\uD83D\uDCCA'}</div>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.g800 }}>Officer Performance</h3>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: C.g400 }}>
                  {officerSummary
                    ? officerSummary.activeOfficers + ' active officers \u00B7 ' + officerSummary.totalInteractions + ' interactions \u00B7 ' + officerSummary.totalUniqueClients + ' unique clients'
                    : 'Loading...'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.g600 }}>Date:</label>
              <input type="date" value={statsDate} onChange={function (e) { setStatsDate(e.target.value); }}
                style={{
                  padding: '6px 10px', borderRadius: 8, border: '2px solid ' + C.g200,
                  fontSize: 12, outline: 'none', cursor: 'pointer', appearance: 'auto',
                }}
              />
            </div>
          </div>

          {/* Officer summary mini-stats */}
          {officerSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase' }}>{'\uD83C\uDFE6'} Loan Officers</p>
                <p style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, color: C.g800 }}>{officerSummary.loanOfficerInteractions}</p>
              </div>
              <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: '#15803d', textTransform: 'uppercase' }}>{'\uD83D\uDCB0'} Collection Officers</p>
                <p style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, color: C.g800 }}>{officerSummary.collectionOfficerInteractions}</p>
              </div>
              <div style={{ padding: '10px 14px', background: C.g50, borderRadius: 8, border: '1px solid ' + C.g200 }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>{'\uD83D\uDC64'} Other</p>
                <p style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, color: C.g800 }}>{officerSummary.otherInteractions}</p>
              </div>
            </div>
          )}

          {/* Officer cards grid */}
          {officerLoading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {[1, 2, 3].map(function (i) {
                return (
                  <div key={i} style={{ background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <Skeleton w={40} h={40} r="50%" />
                      <div style={{ flex: 1 }}><Skeleton w={120} h={14} /><Skeleton w={80} h={20} r={10} /></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Skeleton h={48} r={8} /><Skeleton h={48} r={8} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!officerLoading && officerData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: C.g400 }}>
              <p style={{ fontSize: 36, marginBottom: 8 }}>{'\uD83D\uDCED'}</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: C.g600, marginBottom: 4 }}>No interactions recorded for this date</p>
              <p style={{ fontSize: 12 }}>Try selecting a different date or log new interactions</p>
            </div>
          )}

          {!officerLoading && officerData.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {officerData.map(function (o, i) {
                return (
                  <OfficerCard
                    key={i}
                    officer={o.officer || { firstName: 'Unknown', lastName: '' }}
                    interactions={o.totalInteractions || 0}
                    clients={o.uniqueClients || 0}
                    role={o.officerRole || o.role || 'other'}
                    date={o.date || statsDate}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Loading Skeleton ── */}
      {loading && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid ' + C.g200, overflow: 'hidden' }}>
          {[1, 2, 3, 4, 5, 6].map(function (i) {
            return (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 14px', borderBottom: '1px solid ' + C.g100 }}>
                <Skeleton w={120} h={14} />
                <Skeleton w={70} h={24} r={12} />
                <Skeleton w={130} h={14} />
                <Skeleton w={90} h={24} r={12} />
                <Skeleton w={180} h={14} />
                <Skeleton w={100} h={14} />
                <Skeleton w={70} h={14} />
                <Skeleton w={90} h={14} />
                <Skeleton w={100} h={14} />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Client-clustered Interactions Table ── */}
      {!loading && (
        <div style={{
          background: '#fff', borderRadius: 16, border: '1px solid ' + C.g200,
          overflow: 'hidden', boxShadow: '0 4px 18px rgba(15,23,42,0.04)',
        }}>
          {interactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: C.g400 }}>
              <p style={{ fontSize: 48, marginBottom: 12 }}>{'📭'}</p>
              <p style={{ fontSize: 16, fontWeight: 600, color: C.g600, marginBottom: 4 }}>No clients with interactions found</p>
              <p style={{ fontSize: 13 }}>{hasActiveFilters ? 'Try adjusting your filters' : 'Click "+ New Interaction" to log your first interaction'}</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 230 }}>Client</th>
                    <th style={{ ...thStyle, width: 120, textAlign: 'center' }}>Interactions</th>
                    <th style={{ ...thStyle, width: 150 }}>Methods</th>
                    <th style={{ ...thStyle, width: 150 }}>Sources</th>
                    <th style={{ ...thStyle, width: 220 }}>Latest Interaction</th>
                    <th style={{ ...thStyle, width: 170 }}>Last Activity</th>
                    <th style={{ ...thStyle, width: 90, textAlign: 'center' }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {interactions.map(function (group, idx) {
                    var client = group.client || {};
                    var clientName = getClientName({ client: client });
                    var clientNum = client.clientNumber || '';
                    var clientPhone = client.phone || '';
                    var cid = group._id || getClientId({ client: client });
                    var latest = group.latestInteraction || {};
                    var latestMethod = getMethod(latest);
                    var latestCfg = METHOD_CFG[latestMethod] || { icon: '📌', label: latestMethod || 'Other' };
                    var latestPurpose = getPurpose(latest);
                    var latestOutcome = getOutcome(latest);
                    var sourceCounts = group.sourceCounts || {};
                    var totalForClient = group.interactionCount || 0;
                    var expanded = expandedClientId === cid;
                    var history = clientHistories[cid] || [];
                    var historyLoading = !!clientHistoryLoading[cid];

                    return (
                      <React.Fragment key={cid || ('g' + idx)}>
                        <tr
                          onClick={function () { toggleClient(cid); }}
                          style={{
                            background: expanded ? C.priL : (idx % 2 === 0 ? '#fff' : C.g50),
                            cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          onMouseEnter={function (e) { if (!expanded) e.currentTarget.style.background = '#f8fbff'; }}
                          onMouseLeave={function (e) { e.currentTarget.style.background = expanded ? C.priL : (idx % 2 === 0 ? '#fff' : C.g50); }}
                        >
                          <td style={{ ...tdStyle, padding: '14px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 38, height: 38, borderRadius: 12, background: C.priL, color: C.pri,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 13, fontWeight: 800, flexShrink: 0,
                              }}>{(clientName || 'U')[0]}</div>
                              <div style={{ minWidth: 0 }}>
                                <div
                                  onClick={function (e) { e.stopPropagation(); if (cid) nav('/clients/' + cid); }}
                                  style={{ fontSize: 13, fontWeight: 700, color: C.g800, whiteSpace: 'nowrap', cursor: cid ? 'pointer' : 'default' }}
                                  onMouseEnter={function (e) { if (cid) { e.currentTarget.style.color = C.pri; e.currentTarget.style.textDecoration = 'underline'; } }}
                                  onMouseLeave={function (e) { e.currentTarget.style.color = C.g800; e.currentTarget.style.textDecoration = 'none'; }}
                                >{clientName}</div>
                                <div style={{ marginTop: 3, fontSize: 10, color: C.g400 }}>{clientNum || clientPhone || 'No client number'}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', minWidth: 42, height: 32, padding: '0 10px', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: C.priL, color: C.pri, fontSize: 15, fontWeight: 800 }}>
                              {totalForClient}
                            </div>
                            <div style={{ marginTop: 4, fontSize: 9, color: C.g400 }}>records</div>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'normal' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                              {(group.methods || []).slice(0, 3).map(function (m) {
                                var cfg = METHOD_CFG[m.value] || { icon: '📌', label: m.value };
                                return <Pill key={m.value} label={cfg.icon + ' ' + cfg.label + ' (' + m.count + ')'} color={C.g700} bg={C.g100} small />;
                              })}
                              {(group.methods || []).length > 3 && <Pill label={'+' + ((group.methods || []).length - 3) + ' more'} color={C.g500} bg={C.g100} small />}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'normal' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                              {sourceCounts.communication > 0 && <Pill label={'💬 Communication ' + sourceCounts.communication} color={C.pri} bg={C.priL} small />}
                              {sourceCounts.onboarding > 0 && <Pill label={'📝 Onboarding ' + sourceCounts.onboarding} color={'#7c3aed'} bg={'#f3e8ff'} small />}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 260 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.g700 }}>{latestPurpose || 'General client interaction'}</div>
                            {latestOutcome && <div style={{ marginTop: 4 }}><Pill label={latestOutcome} color={getOutcomeColor(latestOutcome)} bg={getOutcomeColor(latestOutcome) + '18'} small /></div>}
                            {latest.content && <div style={{ marginTop: 5, fontSize: 10, color: C.g500, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{latest.content}</div>}
                          </td>
                          <td style={{ ...tdStyle }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.g700 }}>{latest.createdAt ? formatDate(latest.createdAt) : '—'}</div>
                            <div style={{ marginTop: 3, fontSize: 10, color: C.g400 }}>{relativeTime(latest.createdAt)}</div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <button
                              onClick={function (e) { e.stopPropagation(); toggleClient(cid); }}
                              style={{
                                width: 38, height: 34, borderRadius: 9, border: '1px solid ' + (expanded ? C.pri : C.g200),
                                background: expanded ? C.pri : '#fff', color: expanded ? '#fff' : C.pri,
                                cursor: 'pointer', fontSize: 16, fontWeight: 700,
                              }}
                              title={expanded ? 'Hide interactions' : 'View all interactions'}
                            >{expanded ? '⌃' : '⌄'}</button>
                          </td>
                        </tr>

                        {expanded && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0, borderBottom: '1px solid ' + C.g200, background: '#f8fafc' }}>
                              <div style={{ padding: '16px 18px 18px 64px', borderTop: '1px solid #dbeafe' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: C.g800 }}>All interactions for {clientName}</div>
                                    <div style={{ marginTop: 2, fontSize: 10, color: C.g400 }}>{history.length} interaction{history.length === 1 ? '' : 's'} loaded · onboarding and communication records combined</div>
                                  </div>
                                  {cid && <button onClick={function () { nav('/clients/' + cid); }} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid ' + C.g200, background: '#fff', color: C.pri, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Open Client Profile →</button>}
                                </div>
                                {historyLoading ? (
                                  <div style={{ background: '#fff', border: '1px solid ' + C.g200, borderRadius: 10, padding: 20 }}><Skeleton h={42} r={8} /></div>
                                ) : history.length === 0 ? (
                                  <div style={{ background: '#fff', border: '1px solid ' + C.g200, borderRadius: 10, padding: 20, textAlign: 'center', color: C.g400, fontSize: 12 }}>No interactions found for this client.</div>
                                ) : (
                                  <div style={{ background: '#fff', border: '1px solid ' + C.g200, borderRadius: 12, overflowX: 'auto' }}>
                                    <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Date</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Method</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Source</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Purpose</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Outcome</th>
                                          <th style={{ ...thStyle, background: '#f8fafc', minWidth: 260 }}>Details</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Next Step</th>
                                          <th style={{ ...thStyle, background: '#f8fafc' }}>Created By</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {history.map(function (item, hidx) {
                                          var method = getMethod(item);
                                          var methodCfg = METHOD_CFG[method] || { icon: '📌', label: method || 'Other' };
                                          var outcome = getOutcome(item);
                                          var userName = getUserName(item) || (item.recordedBy ? ((item.recordedBy.firstName || '') + ' ' + (item.recordedBy.lastName || '')).trim() : '');
                                          return (
                                            <tr key={item._id || ('h' + hidx)} style={{ background: hidx % 2 === 0 ? '#fff' : C.g50 }}>
                                              <td style={{ ...tdStyle, fontSize: 10 }}>{item.createdAt ? formatDate(item.createdAt) : '—'}<div style={{ color: C.g400, marginTop: 2 }}>{relativeTime(item.createdAt)}</div></td>
                                              <td style={tdStyle}><Pill label={methodCfg.icon + ' ' + methodCfg.label} color={C.g700} bg={C.g100} small /></td>
                                              <td style={tdStyle}><Pill label={item._source === 'onboarding' ? '📝 Onboarding' : '💬 Communication'} color={item._source === 'onboarding' ? '#7c3aed' : C.pri} bg={item._source === 'onboarding' ? '#f3e8ff' : C.priL} small /></td>
                                              <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 180, fontSize: 11, color: C.g700 }}>{getPurpose(item) || '—'}</td>
                                              <td style={tdStyle}>{outcome ? <Pill label={outcome} color={getOutcomeColor(outcome)} bg={getOutcomeColor(outcome) + '18'} small /> : <span style={{ color: C.g300 }}>—</span>}</td>
                                              <td style={{ ...tdStyle, whiteSpace: 'normal', minWidth: 260, maxWidth: 420, fontSize: 11, color: C.g600, lineHeight: 1.5 }}>{item.content || '—'}</td>
                                              <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 160, fontSize: 11, color: C.pri }}>{getNextSteps(item) || '—'}</td>
                                              <td style={{ ...tdStyle, fontSize: 11, color: C.g600 }}>{userName || '—'}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '8px 0' }}>
          <p style={{ margin: 0, fontSize: 12, color: C.g400 }}>Page {filters.page} of {totalPages} ({totalCount} clients)</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={function () { goToPage(filters.page - 1); }} disabled={filters.page <= 1} style={pageBtnStyle(filters.page <= 1)}>{'\u2190'}</button>
            {generatePageNumbers(filters.page, totalPages).map(function (p, i) {
              if (p === '...') return <span key={'e' + i} style={{ padding: '6px 4px', color: C.g400, fontSize: 13 }}>{'\u2026'}</span>;
              return (
                <button key={p} onClick={function () { goToPage(p); }} style={pageBtnStyle(false, p === filters.page)}>{p}</button>
              );
            })}
            <button onClick={function () { goToPage(filters.page + 1); }} disabled={filters.page >= totalPages} style={pageBtnStyle(filters.page >= totalPages)}>{'\u2192'}</button>
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      <AddModal open={modalOpen} onClose={function () { setModalOpen(false); }} onSaved={handleModalSaved} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHARED STYLES
   ═══════════════════════════════════════════════════════════════ */
function pageBtnStyle(disabled, active) {
  return {
    minWidth: 34, height: 34, borderRadius: 8,
    border: '1px solid ' + (active ? C.pri : C.g200),
    background: active ? C.pri : (disabled ? C.g100 : '#fff'),
    color: active ? '#fff' : (disabled ? C.g300 : C.g700),
    fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
  };
}